// Readers for the case document fields the list and its filters display.
// Field names are the extraction config's human labels, so they carry spaces.

const MISSING = new Set(['information not found', 'not found']);

/** True when an extracted answer is empty or one of the "not found" sentinels. */
export function isMissing(value) {
    if (value === null || value === undefined) return true;
    const text = String(value).trim();
    return text === '' || MISSING.has(text.toLowerCase());
}

const answer = (structuredInfo, key) => structuredInfo?.[key]?.answer;

/** "Last, First" from the two name fields, the legacy single field, or "Unnamed Patient". */
export function getPatientDisplayName(structuredInfo) {
    const first = answer(structuredInfo, 'Patient First Name');
    const last = answer(structuredInfo, 'Patient Last Name');
    const parts = [last, first].filter((p) => !isMissing(p)).map((p) => String(p).trim());
    if (parts.length) return parts.join(', ');
    const legacy = answer(structuredInfo, 'Patient Name');
    if (!isMissing(legacy)) return String(legacy).trim();
    return 'Unnamed Patient';
}

/** Insurance from "Insurance Provider" (rehab, LTACH) or "Insurance Type" (home health). */
export function getInsuranceProvider(structuredInfo) {
    for (const key of ['Insurance Provider', 'Insurance Type']) {
        const value = answer(structuredInfo, key);
        if (!isMissing(value)) return String(value).trim();
    }
    return null;
}

/**
 * Every Medical Record Number the case carries: one per uploaded document,
 * plus the extracted field kept for configs that add it later.
 *
 * A referral packet can carry documents for more than one patient, so a
 * case's MRNs are per document, not one value for the whole case. Both
 * documents belong to the case, so it matches a search for either MRN.
 *
 * Returns unique, trimmed, non-missing values in this order:
 *   1. every `caseItem.identity_check.documents[i].mrn`, in document order
 *   2. `caseItem.structuredInfo['Medical Record Number'].answer`
 */
export function getMrns(caseItem) {
    const documents = caseItem?.identity_check?.documents;
    const raw = [
        ...(Array.isArray(documents) ? documents.map((doc) => doc?.mrn) : []),
        answer(caseItem?.structuredInfo, 'Medical Record Number'),
    ];
    const seen = new Set();
    for (const value of raw) {
        if (isMissing(value)) continue;
        seen.add(String(value).trim());
    }
    return [...seen];
}

const RISKS = new Set(['low', 'medium', 'high']);

/** The backend risk score when it is one of the three known levels. */
export function getRiskScore(caseItem) {
    const risk = caseItem?.risk_score;
    return RISKS.has(risk) ? risk : null;
}

/** Real name text (no sentinels or fallback) for search. Returns { first, last, legacy } with each value as trimmed string or null. */
export function getSearchableNames(structuredInfo) {
    const first = answer(structuredInfo, 'Patient First Name');
    const last = answer(structuredInfo, 'Patient Last Name');
    const legacy = answer(structuredInfo, 'Patient Name');

    return {
        first: isMissing(first) ? null : String(first).trim(),
        last: isMissing(last) ? null : String(last).trim(),
        legacy: isMissing(legacy) ? null : String(legacy).trim(),
    };
}
